import Vue from "vue";
import VirtualList from "vue-virtual-scroll-list";
import { getWebtoonMetaInfo } from "./meta";
import InquiryButton from "@/components/InquiryButton.vue";

// webtoon 이미지들을 require.context로 미리 로드 (WebP 우선, 없으면 PNG)
const webtoonImageContext = require.context(
  "@/assets/webtoon",
  true,
  /\.(webp|png)$/
);

// 웹툰 이미지 타입 정의
interface WebtoonImage {
  id: number;
  imageIndex: number; // 같은 화 내에서의 이미지 순서
  url: any;
  alt: string;
  placeholder?: string;
}

// 웹툰 이미지 그룹 타입 정의 (4개씩 묶음)
interface WebtoonImageGroup {
  id: string; // 고유 ID
  episodeId: number; // 화 번호
  images: WebtoonImage[]; // 최대 4개의 이미지
  isLastGroupOfEpisode?: boolean; // 해당 episode의 마지막 그룹인지 여부
}

// WebtoonItem 컴포넌트 메인 데이터 타입 정의
interface WebtoonItemMainData {
  webtoonImageGroups: WebtoonImageGroup[];
  itemHeight: number;
  totalWebtoons: number;
  isLoadingNext: boolean;
  scrollTriggerObserver: IntersectionObserver | null;
  episodeToastObserver: IntersectionObserver | null;
  shownEpisodes: Set<number>; // 이미 토스트를 표시한 episode들
  isInitialLoad: boolean; // 첫 렌더링 여부
}

// WebtoonItem 컴포넌트 데이터 타입 정의
interface WebtoonItemData {
  loadedImages: Set<number>; // 로드된 이미지 인덱스
  loadingImages: Set<number>; // 로딩 중인 이미지 인덱스
  errorImages: Set<number>; // 에러 발생한 이미지 인덱스
  observer: IntersectionObserver | null;
}

// 웹툰 이미지 아이템 컴포넌트 (4개씩 묶어서 렌더링)
const WebtoonItem = Vue.extend({
  props: {
    index: {
      type: Number,
      default: 0,
    },
    source: {
      type: Object,
      default: () => ({}),
    },
  },
  data(): WebtoonItemData {
    return {
      loadedImages: new Set<number>(),
      loadingImages: new Set<number>(),
      errorImages: new Set<number>(),
      observer: null,
    };
  },
  mounted() {
    const self = this as any;
    const group = this.source as WebtoonImageGroup;

    if (!group || !group.images || group.images.length === 0) return;

    self.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          // 아직 로드되지 않은 이미지들을 모두 프리로드
          group.images.forEach((img, idx) => {
            if (!self.loadedImages.has(idx) && !self.loadingImages.has(idx)) {
              self.preloadImage(img, idx);
            }
          });
        }
      },
      { rootMargin: "800px" }
    );

    this.$nextTick(() => {
      if (this.$refs.container) {
        self.observer?.observe(this.$refs.container as Element);
      }
    });
  },
  methods: {
    preloadImage(image: WebtoonImage, index: number) {
      const self = this as unknown as Vue & WebtoonItemData;
      if (!image || !image.url) {
        self.errorImages.add(index);
        return;
      }

      self.loadingImages.add(index);
      const img = new Image();

      img.onload = () => {
        self.loadedImages.add(index);
        self.loadingImages.delete(index);
        self.errorImages.delete(index);
        self.$forceUpdate();
      };

      img.onerror = () => {
        self.loadingImages.delete(index);
        self.errorImages.add(index);
        self.$forceUpdate();
      };

      const src = typeof image.url === "string" ? image.url : image.url.default;
      img.src = src;
    },
  },
  beforeDestroy() {
    const self = this as unknown as Vue & WebtoonItemData;
    if (self.observer) self.observer.disconnect();
  },
  render(h) {
    const group = this.source as WebtoonImageGroup;
    const self = this as unknown as Vue & WebtoonItemData;

    if (!group || !group.images || group.images.length === 0) {
      return h("div", { style: { display: "none" } });
    }

    // 4개의 이미지를 렌더링
    const imageElements = group.images.map((image, idx) => {
      const loaded = self.loadedImages.has(idx);
      const loading = self.loadingImages.has(idx);
      const error = self.errorImages.has(idx);
      const src =
        image && image.url
          ? typeof image.url === "string"
            ? image.url
            : image.url.default
          : "";
      const alt =
        image?.alt ||
        `웹툰 ${group.episodeId}화 - ${image.imageIndex}번째 이미지`;

      if (loaded && src && !error) {
        return h("v-img", {
          key: `img-${idx}`,
          props: {
            src: src,
            alt: alt,
            contain: true,
            transition: "fade-transition",
          },
          style: {
            width: "100%",
            height: "auto",
          },
        });
      } else if (error) {
        return h(
          "div",
          {
            key: `error-${idx}`,
            style: {
              height: "400px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#1a1a1a",
              color: "#999",
            },
          },
          `이미지를 불러올 수 없습니다 (${group.episodeId}화 - ${image.imageIndex}번째)`
        );
      } else {
        return h(
          "div",
          {
            key: `loading-${idx}`,
            style: {
              height: "600px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000",
            },
          },
          [
            h("v-progress-circular", {
              props: {
                indeterminate: true,
                color: "primary",
                size: 40,
              },
            }),
          ]
        );
      }
    });

    // 그룹에 저장된 isLastGroupOfEpisode 플래그 사용
    const isLastGroupOfEpisode = group.isLastGroupOfEpisode || false;

    return h(
      "div",
      {
        ref: "container",
        class: "webtoon-item-wrapper",
        style: {
          width: "100%",
          display: "flex",
          justifyContent: "center",
          backgroundColor: "#000",
        },
        attrs: {
          "data-episode-id": group.episodeId,
          "data-group-id": group.id,
        },
      },
      [
        h(
          "div",
          {
            class: "webtoon-image-container",
            style: {
              width: "100%",
              maxWidth: "480px",
            },
          },
          imageElements
        ),
      ]
    );
  },
});

export default Vue.extend({
  components: {
    VirtualList,
    WebtoonItem,
    InquiryButton,
  },
  computed: {
    webtoonItemComponent() {
      return WebtoonItem;
    },
  },
  data(): WebtoonItemMainData {
    return {
      webtoonImageGroups: [] as WebtoonImageGroup[],
      itemHeight: 1200,
      totalWebtoons: 19,
      isLoadingNext: false,
      scrollTriggerObserver: null as IntersectionObserver | null,
      episodeToastObserver: null as IntersectionObserver | null,
      shownEpisodes: new Set<number>(),
      isInitialLoad: true, // 첫 렌더링 여부
    };
  },
  mounted() {
    this.$vuetify.theme.dark = false;
    (this as any).initWebtoonList();
    (this as any).setupEpisodeToastObserver();
    (this as any).setupScrollTrigger();
  },
  beforeDestroy() {
    this.$vuetify.theme.dark = true;
    if ((this as any).scrollTriggerObserver) {
      (this as any).scrollTriggerObserver.disconnect();
    }
    if ((this as any).episodeToastObserver) {
      (this as any).episodeToastObserver.disconnect();
    }
  },
  methods: {
    initWebtoonList(this: Vue & WebtoonItemMainData) {
      const startEpisode = Number((this as any).$route.query.episode) || 1;
      // 첫 렌더링 시 시작 화와 다음 화를 함께 로드
      (this as any).addEpisodeToList(startEpisode);
      // 다음 화도 함께 로드 (총 화수 범위 내에서만)
      if (startEpisode < this.totalWebtoons) {
        (this as any).addEpisodeToList(startEpisode + 1);
      }

      // 첫 렌더링 시 첫 번째 episode만 토스트 표시
      this.$nextTick(() => {
        this.shownEpisodes.add(startEpisode);
        (this as any).$toasted.show(`${startEpisode}화`, {
          duration: 1000,
          position: "top-center",
        });

        // observer 시작 전에 약간의 지연을 두어 첫 렌더링 시 이미 보이는 episode는 제외
        setTimeout(() => {
          this.isInitialLoad = false;
          (this as any).observeEpisodeFirstGroups();
        }, 500);
      });
    },
    addEpisodeToList(this: Vue & WebtoonItemMainData, id: number) {
      if (id < 1 || id > this.totalWebtoons) return;

      // 이미 로드된 화인지 확인 (같은 화의 그룹이 있는지 확인)
      if (this.webtoonImageGroups.some((group) => group.episodeId === id))
        return;

      try {
        // 각 화의 모든 이미지를 로드
        const images: WebtoonImage[] = [];

        // require.context에서 해당 화의 모든 이미지 찾기 (WebP 우선)
        const allEpisodeImages = webtoonImageContext
          .keys()
          .filter((path: string) => {
            // {id}/ 폴더 내의 이미지들만 필터링
            const match = path.match(new RegExp(`^\\.\\/${id}\\/`));
            return match !== null;
          });

        // WebP 파일 우선, 없으면 PNG 사용
        const episodeImages = allEpisodeImages
          .filter((path: string) => path.match(/\.webp$/))
          .sort((a: string, b: string) => {
            // 파일명으로 정렬 (1_001.webp, 1_002.webp 순서)
            const aMatch = a.match(/_(\d+)\.webp$/);
            const bMatch = b.match(/_(\d+)\.webp$/);
            const aNum = aMatch ? parseInt(aMatch[1]) : 0;
            const bNum = bMatch ? parseInt(bMatch[1]) : 0;
            return aNum - bNum;
          });

        // WebP가 없으면 PNG 사용
        if (episodeImages.length === 0) {
          const pngImages = allEpisodeImages
            .filter((path: string) => path.match(/\.png$/))
            .sort((a: string, b: string) => {
              const aMatch = a.match(/_(\d+)\.png$/);
              const bMatch = b.match(/_(\d+)\.png$/);
              const aNum = aMatch ? parseInt(aMatch[1]) : 0;
              const bNum = bMatch ? parseInt(bMatch[1]) : 0;
              return aNum - bNum;
            });
          episodeImages.push(...pngImages);
        }

        // 각 이미지를 로드
        episodeImages.forEach((imagePath: string, index: number) => {
          try {
            const imageUrl = webtoonImageContext(imagePath);
            // 파일명에서 이미지 번호 추출 (예: ./1/1_001.webp 또는 ./1/1_001.png -> 1)
            const match = imagePath.match(/_(\d+)\.(webp|png)$/);
            const imageNum = match ? parseInt(match[1]) : index + 1;

            images.push({
              id,
              imageIndex: imageNum,
              url: imageUrl,
              alt: `웹툰 ${id}화 - ${imageNum}번째 이미지`,
            });
          } catch (e) {
            console.error(`Failed to load image: ${imagePath}`, e);
          }
        });

        // 만약 위에서 이미지를 찾지 못했다면, {id}/{id}.webp 또는 {id}/{id}.png 형식 시도 (17화 같은 경우)
        if (images.length === 0) {
          try {
            // WebP 우선 시도
            const webpFallbackPath = `./${id}/${id}.webp`;
            const pngFallbackPath = `./${id}/${id}.png`;

            if (webtoonImageContext.keys().includes(webpFallbackPath)) {
              const imageUrl = webtoonImageContext(webpFallbackPath);
              images.push({
                id,
                imageIndex: 1,
                url: imageUrl,
                alt: `웹툰 ${id}화`,
              });
            } else if (webtoonImageContext.keys().includes(pngFallbackPath)) {
              const imageUrl = webtoonImageContext(pngFallbackPath);
              images.push({
                id,
                imageIndex: 1,
                url: imageUrl,
                alt: `웹툰 ${id}화`,
              });
            }
          } catch (e) {
            console.error(`Failed to load episode ${id}`, e);
            return;
          }
        }

        // 이미지들을 4개씩 묶어서 그룹 생성
        if (images.length > 0) {
          const groups: WebtoonImageGroup[] = [];
          for (let i = 0; i < images.length; i += 4) {
            const groupImages = images.slice(i, i + 4);
            const isLastGroup = i + 4 >= images.length;
            groups.push({
              id: `episode-${id}-group-${Math.floor(i / 4)}`,
              episodeId: id,
              images: groupImages,
              isLastGroupOfEpisode: isLastGroup,
            });
          }

          this.webtoonImageGroups.push(...groups);

          // 각 회차의 첫 번째 그룹을 observer에 등록
          this.$nextTick(() => {
            (this as any).observeEpisodeFirstGroups();
            // DOM 업데이트를 위해 약간의 지연 후 observer 업데이트
            setTimeout(() => {
              (this as any).observeLastEpisodeFirstGroup();
            }, 100);
          });
        } else {
          console.error(`No images found for episode ${id}`);
        }
      } catch (e) {
        console.error(`Failed to load episode ${id}`, e);
      }
    },
    setupEpisodeToastObserver(this: Vue & WebtoonItemMainData) {
      // 각 회차의 첫 번째 그룹이 뷰포트에 들어올 때 토스트 표시
      (this as any).episodeToastObserver = new IntersectionObserver(
        (entries) => {
          // 첫 렌더링 중이면 observer 작동 안 함 (initWebtoonList에서 처리)
          if (this.isInitialLoad) return;

          // 여러 entry가 동시에 intersecting일 수 있으므로, 가장 위에 있는 것만 처리
          const intersectingEntries = entries.filter(
            (entry) => entry.isIntersecting
          );
          if (intersectingEntries.length === 0) return;

          // 가장 위에 있는 entry 찾기 (top 값이 가장 작은 것)
          const topEntry = intersectingEntries.reduce((prev, current) => {
            const prevRect = prev.boundingClientRect;
            const currentRect = current.boundingClientRect;
            return prevRect && currentRect && prevRect.top < currentRect.top
              ? prev
              : current;
          });

          const episodeId = parseInt(
            (topEntry.target as HTMLElement).getAttribute("data-episode-id") ||
              "0",
            10
          );

          // 이미 표시한 episode가 아니면 토스트 표시
          if (episodeId > 0 && !this.shownEpisodes.has(episodeId)) {
            this.shownEpisodes.add(episodeId);
            (this as any).$toasted.show(`${episodeId}화`, {
              duration: 1000,
              position: "top-center",
            });
          }
        },
        {
          root: document.querySelector(".virtual-scroll-container"),
          rootMargin: "-20% 0px -60% 0px", // 화면 상단 20% 영역에 들어올 때 감지
          threshold: 0,
        }
      );
    },
    observeEpisodeFirstGroups(this: Vue & WebtoonItemMainData) {
      if (!(this as any).episodeToastObserver) return;

      // 각 episode의 첫 번째 그룹만 관찰
      const observedEpisodes = new Set<number>();

      this.webtoonImageGroups.forEach((group) => {
        // 각 episode의 첫 번째 그룹만 선택
        if (!observedEpisodes.has(group.episodeId)) {
          observedEpisodes.add(group.episodeId);

          const element = document.querySelector(
            `[data-episode-id="${group.episodeId}"][data-group-id="${group.id}"]`
          );

          if (element && (this as any).episodeToastObserver) {
            (this as any).episodeToastObserver.observe(element);
          }
        }
      });
    },
    setupScrollTrigger(this: Vue & WebtoonItemMainData) {
      // 마지막 회차의 첫 번째 그룹이 뷰포트에 보이면 다음 episode 로드
      (this as any).scrollTriggerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !this.isLoadingNext) {
              const episodeId = parseInt(
                (entry.target as HTMLElement).getAttribute("data-episode-id") ||
                  "0",
                10
              );

              // 마지막 회차의 첫 번째 그룹이 보이면 다음 episode 로드
              if (episodeId > 0) {
                const lastGroup =
                  this.webtoonImageGroups[this.webtoonImageGroups.length - 1];
                const isLastEpisode = lastGroup?.episodeId === episodeId;

                if (isLastEpisode && episodeId < this.totalWebtoons) {
                  (this as any).triggerNextEpisode();
                }
              }
            }
          });
        },
        {
          root: document.querySelector(".virtual-scroll-container"),
          rootMargin: "50% 0px 0px 0px", // 화면 높이의 50% 전에 미리 트리거
          threshold: 0,
        }
      );

      // 초기 관찰 시작
      this.$nextTick(() => {
        (this as any).observeLastEpisodeFirstGroup();
      });
    },
    observeLastEpisodeFirstGroup(this: Vue & WebtoonItemMainData) {
      if (!(this as any).scrollTriggerObserver) return;

      // 마지막 회차의 첫 번째 그룹과 마지막 그룹 모두 관찰
      const lastGroup =
        this.webtoonImageGroups[this.webtoonImageGroups.length - 1];
      if (!lastGroup) return;

      // 마지막 회차의 첫 번째 그룹 찾기
      const firstGroupOfLastEpisode = this.webtoonImageGroups.find(
        (group) => group.episodeId === lastGroup.episodeId
      );

      // 마지막 회차의 마지막 그룹 찾기 (하단 트리거)
      const lastGroupsOfLastEpisode = this.webtoonImageGroups.filter(
        (group) => group.episodeId === lastGroup.episodeId
      );
      const lastGroupOfLastEpisode =
        lastGroupsOfLastEpisode[lastGroupsOfLastEpisode.length - 1];

      // 기존 observer 해제 후 재설정
      (this as any).scrollTriggerObserver.disconnect();

      // 상단 트리거: 마지막 회차의 첫 번째 그룹
      if (firstGroupOfLastEpisode) {
        // 여러 번 시도 (DOM이 아직 업데이트되지 않았을 수 있음)
        const tryObserveFirst = (attempts = 0) => {
          const firstElement = document.querySelector(
            `[data-episode-id="${firstGroupOfLastEpisode.episodeId}"][data-group-id="${firstGroupOfLastEpisode.id}"]`
          );
          if (firstElement) {
            (this as any).scrollTriggerObserver.observe(firstElement);
          } else if (attempts < 3) {
            setTimeout(() => tryObserveFirst(attempts + 1), 50);
          }
        };
        tryObserveFirst();
      }

      // 하단 트리거: 마지막 회차의 마지막 그룹
      if (
        lastGroupOfLastEpisode &&
        lastGroupOfLastEpisode !== firstGroupOfLastEpisode
      ) {
        // 여러 번 시도 (DOM이 아직 업데이트되지 않았을 수 있음)
        const tryObserveLast = (attempts = 0) => {
          const lastElement = document.querySelector(
            `[data-episode-id="${lastGroupOfLastEpisode.episodeId}"][data-group-id="${lastGroupOfLastEpisode.id}"]`
          );
          if (lastElement) {
            (this as any).scrollTriggerObserver.observe(lastElement);
          } else if (attempts < 3) {
            setTimeout(() => tryObserveLast(attempts + 1), 50);
          }
        };
        tryObserveLast();
      }
    },
    onScroll(this: Vue & WebtoonItemMainData, event: Event) {
      if (this.isLoadingNext) return;

      const target = event.target as HTMLElement;
      if (!target) return;

      const { scrollTop, clientHeight, scrollHeight } = target;

      // 바닥에서 화면 높이의 50% 이내로 들어왔는지 체크 (빠른 스크롤 대응)
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);
      const triggerDistance = clientHeight * 0.5;
      if (distanceFromBottom <= triggerDistance) {
        (this as any).triggerNextEpisode();
      }
    },
    triggerNextEpisode(this: Vue & WebtoonItemMainData) {
      if (this.webtoonImageGroups.length === 0) return;
      if (this.isLoadingNext) return; // 이미 로딩 중이면 중복 방지

      // 마지막 그룹의 화 번호 찾기
      const lastGroup =
        this.webtoonImageGroups[this.webtoonImageGroups.length - 1];
      const lastEpisodeId = lastGroup?.episodeId;
      if (lastEpisodeId && lastEpisodeId < this.totalWebtoons) {
        this.isLoadingNext = true;
        (this as any).addEpisodeToList(lastEpisodeId + 1);
        // addEpisodeToList 완료 후 플래그 해제 (DOM 업데이트 대기)
        this.$nextTick(() => {
          setTimeout(() => {
            this.isLoadingNext = false;
            // observer 업데이트 재시도
            this.$nextTick(() => {
              (this as any).observeLastEpisodeFirstGroup();
            });
          }, 100); // 더 빠르게 플래그 해제
        });
      } else {
        this.isLoadingNext = false;
      }
    },
  },
  metaInfo() {
    return getWebtoonMetaInfo((this as any).$route);
  },
});
